import React from 'react'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { cm } from '~/utils/utils'
import type { ApiKeyUsageEntry } from './types'

interface ApiKeyUsageTableProps {
  data: ApiKeyUsageEntry[]
  className?: string
  /** Hide the credits column on plans where credit totals are redacted. */
  showCredits?: boolean
}

const formatLastUsed = (iso: string | null): string => {
  if (!iso) return 'Never'
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return 'Never'
  const diffMs = Date.now() - parsed.getTime()
  // Negative diff = future timestamp (clock skew or backend race). Treat as
  // "Just now" rather than letting Math.floor(diffMs / 60_000) produce a huge
  // positive minutes value that falls through to toLocaleDateString and
  // renders a future date that looks like data corruption.
  if (diffMs < 60_000) return 'Just now'
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

/**
 * Per-API-key usage card shown under the developer Leaderboard.
 *
 * Columns intentionally minimal — credits + last-used are the only signals
 * users care about for an API key. Token scope is not displayed because an
 * API key is a general-purpose credential, not bound to one task type.
 *
 * The parent decides whether to mount this; the component itself renders
 * an empty-state hint if `data` is `[]` so the panel is still legible
 * during validation.
 */
export const ApiKeyUsageTable: React.FC<ApiKeyUsageTableProps> = ({
  data,
  className,
  showCredits = true
}) => {
  const isCompactViewport = useCompactViewport()

  return (
    <div
      className={cm(
        'bg-[rgba(255,255,255,0.5)] border border-white rounded-[24px] flex flex-col',
        !className?.includes('w-') && 'w-[782px]',
        className
      )}
    >
      <div className={cm(isCompactViewport ? 'pt-4 px-4' : 'pt-[24px] pl-[36px]')}>
        <p className="font-['Geist',sans-serif] text-[14px] font-bold leading-[18px] text-tokens-neutral-text-primary">
          API Keys
        </p>
        <p className="mt-1 font-['Geist',sans-serif] text-[12px] font-normal leading-[16px] text-tokens-neutral-text-secondary">
          Activity attributed to API keys in this period.
        </p>
      </div>

      <div className='w-full flex-1 overflow-x-auto'>
        <div
          className={cm(
            'flex flex-col gap-[4px]',
            isCompactViewport ? 'min-w-[480px] px-4 py-3' : 'w-full px-[24px] py-[16px]'
          )}
        >
          <div className='w-full h-[40px] flex items-center gap-[24px] px-[16px]'>
            <div className='flex-1'>
              <p className="font-['Geist_Mono',monospace] text-[12px] font-semibold whitespace-nowrap text-tokens-neutral-text-secondary">
                API Key
              </p>
            </div>
            <div className='w-[100px] text-right'>
              <p className="font-['Geist_Mono',monospace] text-[12px] font-semibold whitespace-nowrap text-tokens-neutral-text-secondary">
                Tasks
              </p>
            </div>
            {showCredits && (
              <div className='w-[120px] text-right'>
                <p className="font-['Geist_Mono',monospace] text-[12px] font-semibold whitespace-nowrap text-tokens-neutral-text-secondary">
                  Credits
                </p>
              </div>
            )}
            <div className='w-[140px] text-right'>
              <p className="font-['Geist_Mono',monospace] text-[12px] font-semibold whitespace-nowrap text-tokens-neutral-text-secondary">
                Last Used
              </p>
            </div>
          </div>

          {data.length === 0 && (
            <div className='w-full flex items-center justify-center px-[16px] py-[24px]'>
              <p className="font-['Geist',sans-serif] text-[12px] font-normal leading-[16px] text-tokens-neutral-text-tertiary">
                No API key activity in this period.
              </p>
            </div>
          )}

          <div className='flex flex-col'>
            {data.map(row => (
              <div
                key={row.id}
                className='w-full flex items-center gap-[24px] px-[16px] py-[8px] rounded-[12px] select-text hover:bg-tokens-neutral-bg-grey-1'
              >
                <div className='flex-1 min-w-0'>
                  <p className="font-['Geist_Mono',monospace] text-[12px] font-semibold leading-[16px] text-tokens-neutral-text-primary truncate">
                    {row.truncated_token || '—'}
                  </p>
                </div>
                <div className='w-[100px] text-right'>
                  <p className="font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-tokens-neutral-text-primary">
                    {(row.tasks ?? 0).toLocaleString()}
                  </p>
                </div>
                {showCredits && (
                  <div className='w-[120px] text-right'>
                    <p className="font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-tokens-neutral-text-primary">
                      {(row.credits ?? 0).toLocaleString()}
                    </p>
                  </div>
                )}
                <div className='w-[140px] text-right'>
                  <p className="font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-tokens-neutral-text-primary">
                    {formatLastUsed(row.last_used_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ApiKeyUsageTable
